import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Badge, Alert } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import { formatDate } from '../utils/dateFormatter';
import { CustomerLink, VehicleLink, TransactionLink } from './EntityLinks';

/**
 * ManagerCalendar component displays upcoming rentals in a chronological list view
 * with conflict detection and filtering options
 */
export function ManagerCalendar() {
  const { apiCall, user } = useOutletContext();
  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [stores, setStores] = useState([]);

  // Filter state - only admins can filter by store
  const [storeId, setStoreId] = useState('');
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 30);
      return d.toISOString().split('T')[0];
    })(),
  });

  // Fetch stores on mount (only for admins who can filter by store)
  useEffect(() => {
    if (!isAdmin) return;

    const loadStores = async () => {
      try {
        const response = await apiCall(`${API_BASE_URL}/stores`);
        if (response.success || response.data) {
          setStores(response.data || []);
        }
      } catch (err) {
        console.error('Failed to load stores:', err);
      }
    };
    loadStores();
  }, [apiCall, isAdmin]);

  // Fetch calendar events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (storeId) params.append('store_id', storeId);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (showConflictsOnly) params.append('show_conflicts_only', 'true');

      const response = await apiCall(`${API_BASE_URL}/calendar/events?${params.toString()}`);

      if (response.success) {
        setEvents(response.data || []);
        setConflicts(response.conflicts || []);
      } else {
        throw new Error(response.error || 'Failed to fetch calendar events');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, storeId, dateRange, showConflictsOnly]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Group events by date for the chronological view
  const groupEventsByDate = (events) => {
    const grouped = {};
    events.forEach(event => {
      const date = event.start;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
    });
    return grouped;
  };

  const groupedEvents = groupEventsByDate(events);
  const sortedDates = Object.keys(groupedEvents).sort();

  // Quick date presets
  const setDatePreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'week':
        start = today.toISOString().split('T')[0];
        end = new Date(today.setDate(today.getDate() + 7)).toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date().toISOString().split('T')[0];
        end = new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0];
        break;
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
        break;
      case 'nextMonth':
        start = new Date(today.getFullYear(), today.getMonth() + 1, 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), today.getMonth() + 2, 0).toISOString().split('T')[0];
        break;
      default:
        return;
    }

    setDateRange({ start, end });
  };

  // Calculate rental duration in days
  const calculateDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Format date for display (e.g., "Mon, Jan 15")
  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2>Upcoming Trips</h2>
          <p className="text-muted mb-0">
            View and manage upcoming rental pickups and drop-offs
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-start">
            {/* Store filter - only shown to admins */}
            {isAdmin && (
              <Col md={2}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Store</Form.Label>
                  <Form.Select
                    size="sm"
                    value={storeId}
                    onChange={(e) => setStoreId(e.target.value)}
                  >
                    <option value="">All Stores</option>
                    {stores?.map(store => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            <Col md={isAdmin ? 2 : 3}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Start Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={isAdmin ? 2 : 3}>
              <Form.Group className="mb-2">
                <Form.Label className="small">End Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={isAdmin ? 3 : 4}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Quick Range</Form.Label>
                <div className="d-flex gap-1">
                  <Button size="sm" variant="outline-secondary" onClick={() => setDatePreset('week')}>
                    7 Days
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setDatePreset('month')}>
                    30 Days
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setDatePreset('thisMonth')}>
                    This Month
                  </Button>
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  id="showConflictsOnly"
                  label="Show Conflicts Only"
                  checked={showConflictsOnly}
                  onChange={(e) => setShowConflictsOnly(e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Summary Stats */}
      <Row className="mb-4">
        <Col md={3}>
          <Card>
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">Total Rentals</span>
                <h4 className="mb-0">{events.length}</h4>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={conflicts.length > 0 ? 'border-warning' : ''}>
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">Conflicts</span>
                <h4 className={`mb-0 ${conflicts.length > 0 ? 'text-warning' : ''}`}>
                  {conflicts.length}
                </h4>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">Active</span>
                <h4 className="mb-0 text-success">
                  {events.filter(e => e.status === 'active').length}
                </h4>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">Completed</span>
                <h4 className="mb-0 text-secondary">
                  {events.filter(e => e.status === 'completed').length}
                </h4>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Chronological Event List */}
      {loading ? (
        <Alert variant="info">Loading calendar events...</Alert>
      ) : events.length === 0 ? (
        <Alert variant="secondary">
          No rentals found for the selected date range and filters.
        </Alert>
      ) : (
        <div className="chronological-list">
          {sortedDates.map(date => (
            <div key={date} className="mb-4">
              {/* Date Header */}
              <h5 className="border-bottom pb-2 mb-3" style={{ color: '#667eea' }}>
                {formatEventDate(date)}
              </h5>

              {/* Events for this date */}
              {groupedEvents[date].map(event => (
                <Card
                  key={event.id}
                  className={`mb-3 ${event.conflict ? 'border-warning' : ''}`}
                  style={event.conflict ? { borderWidth: '2px' } : {}}
                >
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div className="d-flex align-items-start">
                          <div>
                            <h6 className="mb-1">
                              <CustomerLink
                                customerId={event.customer.id}
                                customerName={`${event.customer.first_name} ${event.customer.last_name}`}
                                className="btn btn-link btn-sm p-0"
                              />
                              {' - '}
                              <VehicleLink
                                vehicleId={event.vehicle.id}
                                vehicleInfo={`${event.vehicle.license_plate} (${event.vehicle.make} ${event.vehicle.model})`}
                                className="btn btn-link btn-sm p-0"
                              />
                            </h6>
                            <div className="small text-muted">
                              {formatDate(event.start)} - {formatDate(event.end)}
                              ({calculateDuration(event.start, event.end)} days)
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col md={3} className="text-center">
                        <div className="small text-muted">Amount</div>
                        <div className="fw-bold">${event.amount.toFixed(2)}</div>
                      </Col>
                      <Col md={3} className="text-end">
                        <Badge bg={event.status === 'active' ? 'success' : 'secondary'} className="me-2">
                          {event.status.toUpperCase()}
                        </Badge>
                        {event.conflict && (
                          <Badge bg="warning" text="dark">
                            CONFLICT
                          </Badge>
                        )}
                        <div className="mt-2">
                          <TransactionLink
                            transactionId={event.id}
                            className="btn btn-outline-primary btn-sm"
                          >
                            View Details
                          </TransactionLink>
                        </div>
                      </Col>
                    </Row>

                    {/* Conflict Warning */}
                    {event.conflict && event.conflicts_with && event.conflicts_with.length > 0 && (
                      <Alert variant="warning" className="mt-3 mb-0 py-2">
                        <small>
                          <strong>Conflict:</strong> This vehicle overlaps with rental(s):{' '}
                          {event.conflicts_with.map((conflictId, idx) => (
                            <span key={conflictId}>
                              <TransactionLink transactionId={conflictId} className="btn btn-link btn-sm p-0">
                                #{conflictId}
                              </TransactionLink>
                              {idx < event.conflicts_with.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </small>
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}

export default ManagerCalendar;
